import moment from 'moment';
import type { DiscountCardProps, DiscountCardStatus } from '@tempmee/react-blocks';
import type { IDiscountCode } from '~/domain/DiscountCode';

/** Dates render as the office reads them elsewhere in the portal: 1/15/2026. */
const DISPLAY_DATE_FORMAT = 'M/D/YYYY';

/**
 * Formats an API timestamp for display, or returns `undefined` when there is
 * nothing meaningful to show.
 *
 * Guards Go's zero time as well as a missing value: a `time.Time` field that was
 * never set marshals as `0001-01-01T00:00:00Z`, which would otherwise render as
 * a straight-faced "1/1/1" in the card.
 */
export const formatDisplayDate = (isoDate?: string | null): string | undefined => {
  if (!isoDate) {
    return undefined;
  }

  // Strict ISO parsing: the API only ever sends ISO-8601, and moment's loose
  // mode falls back to `new Date()` with a deprecation warning on anything else.
  const parsed = moment(isoDate, moment.ISO_8601, true);

  if (!parsed.isValid() || parsed.year() <= 1) {
    return undefined;
  }

  return parsed.format(DISPLAY_DATE_FORMAT);
};

/**
 * The status chip a code renders as. The API sends no status field — the chip is
 * derived from `usesRemaining`, `live`, `endsAt` and `officeEligible`, in this
 * order (first match wins):
 *
 * 1. **used** — the per-office cap is spent (`usesRemaining === 0`). Wins over
 *    everything: a fully-used code the holder has burned is USED even if the
 *    campaign has since ended or the office has aged out — that is the fact the
 *    office acts on.
 * 2. **expired** — the campaign is no longer live, or `endsAt` has passed. Ended
 *    for everyone, so it outranks the office-specific `ineligible`.
 * 3. **ineligible** — the campaign is still live but this office no longer
 *    matches its audience (`live: true, officeEligible: false`). Nothing ended
 *    and nothing was spent; the code simply is not for them, and a claim would
 *    decline. Never fold this into `expired` — the promotion has not ended.
 * 4. **active** — anything else, including every uncapped code (no
 *    `usesRemaining` at all ⇒ unlimited ⇒ never "used").
 *
 * @param code - one applied-code row from the API
 * @param now - injectable clock, so expiry is testable without freezing time globally
 */
export const deriveDiscountStatus = (
  code: Pick<IDiscountCode, 'live' | 'usesRemaining' | 'endsAt' | 'officeEligible'>,
  now: Date = new Date(),
): DiscountCardStatus => {
  // Strict equality on purpose: an uncapped code sends `usesRemaining: null`,
  // which must never read as a spent cap.
  if (code.usesRemaining === 0) {
    return 'used';
  }

  if (!code.live) {
    return 'expired';
  }

  if (code.endsAt && new Date(code.endsAt).getTime() <= now.getTime()) {
    return 'expired';
  }

  // Live campaign, not past its end date, but this office has dropped out of the
  // audience — a claim from here declines, so say so rather than showing ACTIVE.
  if (!code.officeEligible) {
    return 'ineligible';
  }

  return 'active';
};

/**
 * Maps one applied-code row onto the presentational `DiscountCard` contract.
 *
 * The card is display-only and hides a row on `undefined`, so every nullable
 * field is normalised from the BE's explicit `null` to `undefined` here: no
 * `usesRemaining` hides the counter, no `endsAt` hides the expiry line, no
 * `appliedBy` hides the name but keeps the date. `null` happens to satisfy the
 * card's own `!= null` guard, but it does not satisfy its prop types — the
 * normalisation is what keeps that honest.
 *
 * `officeEligible` reaches the card through `deriveDiscountStatus`, which turns a
 * live-but-ineligible row into the `ineligible` chip (added to
 * `DiscountCardStatus` in @tempmee/core 1.16 / react-blocks 0.12) rather than
 * the misleading ACTIVE it used to render as.
 */
export const toDiscountCardProps = (
  code: IDiscountCode,
  now: Date = new Date(),
): DiscountCardProps => ({
  code: code.code,
  description: code.description,
  status: deriveDiscountStatus(code, now),
  usesRemaining: code.usesRemaining ?? undefined,
  expiresLabel: formatDisplayDate(code.endsAt),
  // An applier the identity mirror has not caught up with comes back as "" —
  // fall through to undefined so the card drops the name row instead of
  // rendering an empty line.
  appliedByLabel: code.appliedBy || undefined,
  appliedAtLabel: formatDisplayDate(code.appliedAt),
});
